import { useEffect, useState } from "react"

import { SOCKET_EVENTS } from "@domo/shared"
import { getSocket } from "../../lib/socket"
import { usePlayerStore } from "../../stores/usePlayerStore"

// 2026-05-06 — Prompt de Reconfiguración.
//
// Aparece cuando el Tripulante camina sobre el portal dorado lateral.
// Reemplaza al botón "ELEGIR AVATAR" del menú top-right como acción
// in-world: para cambiar firma, hay que ir físicamente al portal.
//
// Flujo al tap:
//   1. Cliente llama `beginAscent(durationMs)` → fase pasa a
//      "ascending", el cuerpo del avatar se desmonta y aparece el
//      AscentOrb (bola dorada que sube al cielo).
//   2. Después de `ASCENT_DURATION_MS` el cliente emite
//      RECONFIGURE_REQUEST al server. El server limpia la firma
//      persistida y emite VOID_RETURN.
//   3. useSocket recibe VOID_RETURN y llama `returnToVoid()` → fase
//      pasa a "void", se vuelve al rito del Estado Cero para elegir
//      una firma nueva.
//
// Antes (v6) llamábamos `returnToVoid()` directamente desde el cliente
// sin emitir al server: el server seguía creyendo que el Tripulante
// estaba incarnated, así que cuando enviaba SPAWN_REQUEST con la nueva
// firma el server lo rechazaba y el cliente quedaba trabado en
// "DESCENDIENDO". Ahora la cadena pasa por el server y el flujo se
// completa.
//
// Se renderea como pill flotante en bottom-center (mobile y desktop)
// con animación discreta de aparición. NO oscurece la pantalla:
// queremos seguir viendo el portal y el campo durante la decisión.

const ASCENT_DURATION_MS = 1800

const noSelect = {
    userSelect: "none" as const,
    WebkitUserSelect: "none" as const,
    WebkitTouchCallout: "none" as const,
    WebkitTapHighlightColor: "transparent",
}

export function ElectionPortalPrompt() {
    const isOnPortal = usePlayerStore((s) => s.isOnElectionPortal)
    const phase = usePlayerStore((s) => s.phase)
    const beginAscent = usePlayerStore((s) => s.beginAscent)
    const [submitting, setSubmitting] = useState(false)

    // 2026-05-06 — Reset del flag `submitting` cuando la fase deja de
    // ser "ascending". Antes el flag quedaba en true después de la
    // primera reconfiguración: el componente NO se desmonta cuando
    // retorna null (sólo deja de pintar), por lo que el state local
    // sobrevivía al ciclo void → selector → descending → incarnated.
    // Resultado: la segunda vez que el Tripulante pisaba el portal,
    // el botón estaba bloqueado por el `submitting` viejo y el click
    // hacía no-op. Con este efecto, en cuanto la fase pasa a void
    // (o a cualquier cosa distinta de ascending) el flag se libera y
    // el siguiente ciclo arranca limpio.
    useEffect(() => {
        if (phase !== "ascending" && submitting) {
            setSubmitting(false)
        }
    }, [phase, submitting])

    if (!isOnPortal || phase !== "incarnated") return null

    const onAscend = () => {
        if (submitting) return
        setSubmitting(true)
        // 1. Disparar la animación local de transmutación (cuerpo →
        //    orbe → cielo). Eso desmonta el cuerpo del avatar de
        //    inmediato; el AscentOrb se monta en su lugar.
        beginAscent(ASCENT_DURATION_MS)
        // 2. Emitir al server al final de la animación. El server
        //    limpia firma persistida y devuelve VOID_RETURN; useSocket
        //    llama returnToVoid() y el rito del Estado Cero arranca.
        window.setTimeout(() => {
            const socket = getSocket()
            if (socket?.connected) {
                socket.emit(SOCKET_EVENTS.RECONFIGURE_REQUEST)
            }
            // submitting lo libera el useEffect arriba al cambiar la
            // fase fuera de "ascending".
        }, ASCENT_DURATION_MS)
    }

    return (
        <div
            className="fixed inset-x-0 z-30 flex justify-center"
            style={{
                bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
                pointerEvents: "none",
                ...noSelect,
            }}
        >
            <style>{`
                @keyframes electionPromptIn {
                    0%   { opacity: 0; transform: translateY(14px) scale(0.96); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
            <button
                type="button"
                onClick={onAscend}
                disabled={submitting}
                style={{
                    pointerEvents: "auto",
                    padding: "14px 28px",
                    borderRadius: 999,
                    border: "1px solid rgba(212,175,55,0.65)",
                    background:
                        "linear-gradient(90deg, rgba(212,175,55,0.95), rgba(255,210,122,0.85))",
                    color: "#1A0F02",
                    fontFamily: "Inter, system-ui, sans-serif",
                    fontSize: 12.5,
                    fontWeight: 600,
                    letterSpacing: "0.26em",
                    textTransform: "uppercase",
                    boxShadow:
                        "0 0 28px rgba(212,175,55,0.55), 0 6px 20px rgba(0,0,0,0.55)",
                    cursor: "pointer",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    animation:
                        "electionPromptIn 280ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
                    ...noSelect,
                }}
                aria-label="Cambiar firma del Avatar"
            >
                ◈ Cambiar firma del Avatar
            </button>
        </div>
    )
}
